import { Component, Input, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
} from '@angular/forms';

export interface FormInputFieldConfig {
  type:
    | 'text'
    | 'password'
    | 'email'
    | 'number'
    | 'url'
    | 'tel'
    | 'select'
    | 'radio'
    | 'checkbox'
    | 'textarea';
  label: string;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  readonly?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  options?: Array<{
    value: any;
    label: string;
  }>;
  rows?: number;
  labelColClass?: string;
  inputColClass?: string;
  showValidation?: boolean;
  customValidationMessages?: {
    required?: string;
    pattern?: string;
    minlength?: string;
    maxlength?: string;
    email?: string;
    whitespace?: string;
    [key: string]: string;
  };
}

@Component({
  selector: 'app-form-input-field',
  templateUrl: './form-input-field.component.html',
  styleUrls: ['./form-input-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormInputFieldComponent),
      multi: true,
    },
  ],
})
export class FormInputFieldComponent implements ControlValueAccessor {
  @Input() config!: FormInputFieldConfig;
  @Input() control!: FormControl;
  @Input() formControlName?: string;

  value: any;
  disabled = false;

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(value: any): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  get labelColClass(): string {
    return this.config.labelColClass || 'col-md-3';
  }

  get inputColClass(): string {
    return this.config.inputColClass || 'col-md-6';
  }

  get isInvalid(): boolean {
    return (
      this.control &&
      this.control.invalid &&
      (this.control.dirty || this.control.touched)
    );
  }

  get errorMessage(): string {
    if (!this.control || !this.control.errors) {
      return '';
    }

    const errors = this.control.errors;
    const customMessages = this.config.customValidationMessages || {};

    if (errors['required']) {
      return customMessages['required'] || 'admin.global.field.required';
    }
    if (errors['pattern']) {
      return customMessages['pattern'] || 'admin.store.payment.valueNotValid';
    }
    if (errors['minlength']) {
      return customMessages['minlength'] || 'admin.store.payment.valueNotValid';
    }
    if (errors['maxlength']) {
      return customMessages['maxlength'] || 'admin.store.payment.valueNotValid';
    }
    if (errors['email']) {
      return customMessages['email'] || 'admin.store.payment.valueNotValid';
    }
    if (errors['whitespace']) {
      return (
        customMessages['whitespace'] || 'admin.store.payment.valueNotValid'
      );
    }

    const firstErrorKey = Object.keys(errors)[0];
    return customMessages[firstErrorKey] || 'admin.store.payment.valueNotValid';
  }

  get errorMessageParams(): object | null {
    if (!this.control || !this.control.errors) {
      return null;
    }
    const errors = this.control.errors;
    if (errors['maxlength']) {
      return { maxLength: errors['maxlength'].requiredLength };
    }
    if (errors['minlength']) {
      return { minLength: errors['minlength'].requiredLength };
    }
    return null;
  }
}
